/**
 * Unified pulse animation system for all widgets
 * Provides consistent visual indicators for conversation state across all widget types
 */

export type ButtonState = "idle" | "connecting" | "recording" | "disabled";

/**
 * CSS keyframes for pulse animations
 * These animations create expanding rings around the widget
 */
export const PULSE_ANIMATION_STYLES = `
  @keyframes avatar-button-pulse-blue {
    0% {
      transform: scale(1);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 0 0 0 rgba(59, 130, 246, 0.6);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 0 0 10px rgba(59, 130, 246, 0);
      opacity: 0.8;
    }
    100% {
      transform: scale(1);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 0 0 0 rgba(59, 130, 246, 0);
      opacity: 1;
    }
  }

  @keyframes avatar-button-pulse-yellow {
    0% {
      transform: scale(1);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 0 0 0 rgba(234, 179, 8, 0.7);
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 0 0 10px rgba(234, 179, 8, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 0 0 0 rgba(234, 179, 8, 0);
    }
  }

  @keyframes avatar-pulse-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

/**
 * Инжектирует CSS-стили анимаций в документ, если их еще нет
 */
export const injectPulseAnimations = (
  styleId = "avatar-pulse-animations"
): void => {
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = PULSE_ANIMATION_STYLES;
    document.head.appendChild(style);
  }
};

/**
 * Определяет, какую анимацию применить на основе состояния
 * Приоритет: recording > connecting > widget active > none
 *
 * @param buttonState Текущее состояние кнопки
 * @param isActive Активна ли конверсация
 * @returns Строка animation CSS свойства
 */
export const getAnimationForState = (
  buttonState: ButtonState,
  isActive: boolean
): string => {
  // Pulse only when actively recording or connecting — not on idle
  if (buttonState === "recording") {
    return "avatar-button-pulse-blue 2s infinite";
  }
  if (buttonState === "connecting") {
    return "avatar-button-pulse-yellow 2s infinite";
  }
  return "none";
};

/**
 * Получает цвет пульсирующего кольца на основе состояния
 * Используется для UI-элементов, которые показывают статус
 */
export const getPulseColorForState = (
  buttonState: ButtonState,
  isActive: boolean
): { color: string; rgba: string } => {
  if (buttonState === "recording") {
    return { color: "#ef4444", rgba: "rgba(239, 68, 68, 0.7)" }; // Red
  }
  if (buttonState === "connecting") {
    return { color: "#eab308", rgba: "rgba(234, 179, 8, 0.7)" }; // Yellow
  }
  if (isActive) {
    return { color: "#3b82f6", rgba: "rgba(59, 130, 246, 0.7)" }; // Blue
  }
  return { color: "#6b7280", rgba: "rgba(107, 113, 128, 0.5)" }; // Gray
};

/**
 * Badge colors for different widget states
 */
export const BADGE_COLORS = {
  active: "#10b981", // Green - widget is active/connected
  disabled: "#ef4444", // Red - widget is disabled
  recording: "#dc2626", // Dark red - currently recording
  connecting: "#eab308", // Yellow - connecting
} as const;
